import type { ISignInPageProps } from './index';
import type { ISignInPageStateProps } from './useSignInPage';

import React from 'react';

import HeadContentSection from '$/components/blocks/HeadContentSection';
import SignInPage from '$/components/blocks/SignInPage';
import Button from '$/components/elements/Button';
import ButtonContainer from '$/components/elements/ButtonContainer';
import ContactPanel from '$/components/elements/ContactPanel';
import Container from '$/components/elements/Container';
import Loader from '$/components/elements/Loader';
import TextInputQuestion from '$/components/elements/Questions/TextInputQuestion';
import {
  H5Typography,
  H6Typography,
  Sub1Typography,
} from '$/components/elements/Typography';

interface ISignInPageViewProps
  extends ISignInPageProps,
    ISignInPageStateProps {}

const SignInPageView = (props: ISignInPageViewProps) => {
  if (props.submitMutation.status === 'success') {
    return (
      <SignInPage>
        <HeadContentSection>
          <Container>
            <HeadContentSection.Wrapper>
              <HeadContentSection.Title bold as="h1" color="grey7">
                <HeadContentSection.Underline>
                  Herzlich willkommen!
                </HeadContentSection.Underline>
              </HeadContentSection.Title>
              <HeadContentSection.StyledImage
                src="/images/roller-skating.svg"
                alt="roller skating"
                mobileBigger
              />
            </HeadContentSection.Wrapper>
          </Container>
        </HeadContentSection>
        <SignInPage.Header>
          <H5Typography bold color="grey7" as="h1">
            Hier deinen Zugangslink anfordern
          </H5Typography>
          <H6Typography as="h2" color="brandGreen">
            E-Mail wurde verschickt
          </H6Typography>
        </SignInPage.Header>
        <SignInPage.Section>
          <Container>
            <ButtonContainer columns="1fr" style={{ marginTop: '8px' }}>
              {props.page === 'logout' ? (
                <Button
                  title="Zugangslink noch mal erhalten"
                  link={{
                    href: '/signin',
                  }}
                />
              ) : (
                <Button
                  title="Zugangslink noch mal erhalten"
                  buttonProps={{
                    as: 'button',
                    onClick: props.submitMutation.reset,
                  }}
                />
              )}
            </ButtonContainer>
          </Container>
        </SignInPage.Section>
        <SignInPage.Section>
          <Sub1Typography as="h2" color="grey7">
            Du hast noch kein Nutzerkonto?
          </Sub1Typography>
          <Button
            variant="text"
            title="Bewirb dich jetzt für deine Studienfinanzierung."
            buttonProps={{
              style: { padding: 0, marginTop: 0 },
            }}
            link={{
              href: '/registration',
            }}
          />
        </SignInPage.Section>
      </SignInPage>
    );
  }

  if (props.page === 'logout') {
    return (
      <SignInPage>
        <HeadContentSection>
          <Container>
            <HeadContentSection.Wrapper>
              <HeadContentSection.Title bold as="h1" color="grey7">
                <span>Du hast dich</span>
                <HeadContentSection.Underline>
                  erfolgreich abgemeldet.
                </HeadContentSection.Underline>
                <br />
                <span>Bis zum nächsten Mal!</span>
              </HeadContentSection.Title>
              <HeadContentSection.StyledImage
                src="/images/composition-9-green.svg"
                alt="girl sitting"
                mobileBigger
              />
            </HeadContentSection.Wrapper>
          </Container>
        </HeadContentSection>
        <Container>
          <form onSubmit={props.form.handleSubmit(props.onSubmit)}>
            <SignInPage.Header>
              {props.statusText.header && (
                <H5Typography
                  bold
                  color={props.statusText.header.color}
                  as="h1"
                >
                  Zurück zu deinem Nutzerkonto
                </H5Typography>
              )}
            </SignInPage.Header>
            <div>
              <TextInputQuestion
                name={'email'}
                control={props.form.control}
                type={'email'}
                placeholder={'Deine E-Mail-Adresse'}
                errorMessage="Diese E-Mail-Adresse gehört zu keinem Nutzerkonto"
                iconPosition="end"
              />
            </div>
            <ButtonContainer
              columns="1fr"
              style={{ marginTop: '8px', width: '100%' }}
            >
              <Button
                title="Zugangslink erhalten"
                disabled={
                  !!props.form.formState.errors?.email ||
                  props.form.formState.isSubmitting
                }
                isLoading={props.submitMutation.isLoading}
                loader={<Loader />}
                buttonProps={{
                  as: 'button',
                  type: 'submit',
                }}
              />
            </ButtonContainer>
            {props.showContactPanel && <ContactPanel />}
          </form>
        </Container>
      </SignInPage>
    );
  }

  return (
    <SignInPage>
      <HeadContentSection>
        <Container>
          <HeadContentSection.Wrapper>
            <HeadContentSection.Title bold as="h1" color="grey7">
              Hier den
              <HeadContentSection.Underline>
                Zugangslink für dein
              </HeadContentSection.Underline>
              <HeadContentSection.Underline>
                Nutzerkonto
              </HeadContentSection.Underline>
              anfordern.
            </HeadContentSection.Title>
            <HeadContentSection.StyledImage
              src="/images/roller-skating.svg"
              alt="roller skating"
              mobileBigger
            />
          </HeadContentSection.Wrapper>
        </Container>
      </HeadContentSection>
      <Container>
        <form onSubmit={props.form.handleSubmit(props.onSubmit)}>
          <SignInPage.Header>
            {props.statusText.header && (
              <H5Typography bold color={props.statusText.header.color} as="h1">
                {props.statusText.header.text}
              </H5Typography>
            )}
            {props.statusText.sub && (
              <Sub1Typography
                as="h2"
                color={props.statusText.sub.color}
                align="center"
              >
                {props.statusText.sub.text}
              </Sub1Typography>
            )}
          </SignInPage.Header>
          <div>
            <TextInputQuestion
              name={'email'}
              control={props.form.control}
              type={'email'}
              placeholder={'Deine E-Mail-Adresse'}
              errorMessage="Diese E-Mail-Adresse gehört zu keinem Nutzerkonto"
              iconPosition="end"
            />
          </div>
          <ButtonContainer
            columns="1fr"
            style={{ marginTop: '8px', width: '100%' }}
          >
            <Button
              title="Zugangslink erhalten"
              disabled={
                !!props.form.formState.errors?.email ||
                props.form.formState.isSubmitting
              }
              isLoading={props.submitMutation.isLoading}
              loader={<Loader />}
              buttonProps={{
                as: 'button',
                type: 'submit',
              }}
            />
          </ButtonContainer>
          <div style={{ margin: '64px 0' }}>
            <Sub1Typography
              as="h2"
              color="grey7"
              style={{ textAlign: 'center' }}
            >
              Du hast noch kein Nutzerkonto?
            </Sub1Typography>
            <Button
              variant="text"
              title="Bewirb dich jetzt für deine Studienfinanzierung."
              buttonProps={{
                style: { padding: 0, marginTop: 0 },
              }}
              link={{
                href: '/registration',
              }}
            />
          </div>
          {props.showContactPanel && <ContactPanel />}
        </form>
      </Container>
    </SignInPage>
  );
};

export default SignInPageView;
